import { useState, useEffect } from 'react';
import {
  Phone,
  Clock,
  Calendar,
  Search,
  Filter,
  Download,
  Play,
  FileText,
  User,
  LogOut,
  X,
  Trash2,
  ChevronDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { getBrand } from '../brand';
import Stats from '../components/Stats';
import CallDetails from '../components/CallDetails';

const LIMIT = 100;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const brand = getBrand();

  const [calls, setCalls] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState({ total_calls: 0, total_pulse: 0, avg_pulse: 0, active_agents: 0 });

  const [workflows, setWorkflows] = useState([]);
  const [displayConfigs, setDisplayConfigs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [workflowId, setWorkflowId] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedCall, setSelectedCall] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  useEffect(() => {
    fetchCalls();
    fetchStats();
  }, [fromDate, toDate, workflowId]);

  useEffect(() => {
    filterCalls();
  }, [searchTerm, calls]);

  const fetchCalls = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams();
      params.append('limit', LIMIT);
      params.append('offset', loadMore ? offset : 0);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (workflowId !== 'all') params.append('workflow_id', workflowId);

      const res = await api.get(`/calls?${params.toString()}`);
      const { data, total, has_more } = res.data;

      if (loadMore) {
        setCalls((prev) => [...prev, ...data]);
        setOffset((prev) => prev + data.length);
      } else {
        setCalls(data);
        setOffset(data.length);
      }
      setTotal(total);
      setHasMore(has_more);
    } catch (error) {
      console.error('Error fetching calls:', error);
      alert('Failed to load calls');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (workflowId !== 'all') params.append('workflow_id', workflowId);

      const res = await api.get(`/calls/stats?${params.toString()}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const res = await api.get('/calls/workflows/list');
      setWorkflows(res.data);

      const configs = {};
      for (const wf of res.data) {
        try {
          const configRes = await api.get(`/display-config/workflows/${wf.workflow_id}`);
          if (configRes.data?.length) {
            configs[wf.workflow_id] = configRes.data;
          }
        } catch (err) {
          console.log(`No display config for ${wf.workflow_id}`);
        }
      }
      setDisplayConfigs(configs);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const filterCalls = () => {
    let result = calls;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter((c) => {
        return (
          c.workflow_run_id?.toLowerCase().includes(q) ||
          c.workflow_name?.toLowerCase().includes(q) ||
          JSON.stringify(c.gathered_context)?.toLowerCase().includes(q) ||
          JSON.stringify(c.initial_context)?.toLowerCase().includes(q)
        );
      });
    }

    setFiltered(result);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/calls/${id}`);
      setCalls((prev) => prev.filter((c) => c.id !== id));
      setFiltered((prev) => prev.filter((c) => c.id !== id));
      setDeleteId(null);
      fetchStats();
    } catch (error) {
      console.error('Error deleting call:', error);
      alert(error.response?.data?.detail || 'Failed to delete call');
    }
  };

  const openTranscript = async (call) => {
    setSelectedCall(call);
    setTranscriptOpen(true);
    setTranscriptLoading(true);
    setTranscript('');

    if (!call.transcript_url) {
      setTranscript('No transcript available');
      setTranscriptLoading(false);
      return;
    }

    try {
      const url = call.transcript_url.startsWith('http')
        ? call.transcript_url
        : `https://${call.transcript_url}`;
      const res = await fetch(url);
      const text = await res.text();

      const formatted = text
        .split('\n')
        .filter(Boolean)
        .map((line) => {
          let clean = line.replace(/^\[[\d\-T:+.]+\]\s*/, '');
          clean = clean.replace(/[●▸]\s+\w+\s+\[\s*/, '').replace(/\s*\]\s*$/, '');
          if (clean.startsWith('assistant:')) return `Agent: ${clean.replace('assistant:', '').trim()}`;
          if (clean.startsWith('user:')) return `Customer: ${clean.replace('user:', '').trim()}`;
          return clean;
        })
        .join('\n\n');

      setTranscript(formatted || text);
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setTranscript('Failed to load transcript');
    } finally {
      setTranscriptLoading(false);
    }
  };

  const openExport = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setExportTo(today.toISOString().split('T')[0]);
    setExportFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setExportOpen(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append('from_date', exportFrom);
      params.append('to_date', exportTo);
      if (workflowId !== 'all') params.append('workflow_id', workflowId);

      const res = await api.get(`/export/csv?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calls_export.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setExportOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatPulse = (seconds) => {
    if (!seconds) return '0 pulses';
    const pulses = Math.ceil(seconds / 60);
    return `${pulses} pulse${pulses === 1 ? '' : 's'}`;
  };

  const getCustomerName = (call) => {
    try {
      const ctx =
        typeof call.gathered_context === 'string'
          ? JSON.parse(call.gathered_context)
          : call.gathered_context;
      const name = ctx?.customer_name;
      if (name) return name;

      const phone = ctx?.phone || ctx?.phone_number || call.initial_context?.phone || call.initial_context?.phone_number;
      if (phone) return String(phone);

      return `Call ${call.workflow_run_id?.slice(-6) || call.id}`;
    } catch {
      return `Call ${call.workflow_run_id?.slice(-6) || call.id}`;
    }
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const formatFieldValue = (value, type) => {
    if (!value) return null;
    switch (type) {
      case 'phone':
        const phone = String(value).replace(/\D/g, '');
        return phone.length === 10 ? `+91-${phone.slice(0, 5)}-${phone.slice(5)}` : value;
      case 'currency':
        return `₹${value}`;
      case 'datetime':
      case 'timestamp':
        try {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            return d.toLocaleString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
          }
        } catch (e) {
          return value;
        }
        return value;
      default:
        const str = String(value);
        return str.length > 40 ? str.substring(0, 37) + '...' : str;
    }
  };

  const getDisplayFields = (call) => {
    const config = displayConfigs[call.workflow_id];
    if (!config) return [];
    return config
      .filter((f) => f.show_in_list)
      .map((f) => {
        const value = getNestedValue(call, f.field_path);
        if (!value) return null;
        return {
          icon: f.icon,
          label: f.display_label,
          value: formatFieldValue(value, f.field_type),
        };
      })
      .filter(Boolean);
  };

  const fullUrl = (url) => (url ? (url.startsWith('http') ? url : `https://${url}`) : null);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-surface border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: brand.primary }}
              >
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-text-main leading-tight">{brand.appName}</h1>
                <p className="text-xs text-text-muted">{brand.tagline}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {user && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: brand.primaryLight }}
                  >
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-sm leading-tight">
                    <span className="block text-text-main font-medium">{user.name}</span>
                    <span className="block text-xs text-text-muted capitalize">{user.role}</span>
                  </div>
                </div>
              )}
              <button
                onClick={openExport}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-text-main hover:bg-slate-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm">Export</span>
              </button>
              <button
                onClick={() => { fetchCalls(); fetchStats(); }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: brand.accent }}
              >
                <span className="text-sm font-medium">Refresh</span>
              </button>
              <button
                onClick={logout}
                className="p-2 rounded-lg border border-slate-200 text-text-muted hover:bg-slate-50 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Stats stats={stats} />

        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="text"
                placeholder="Search calls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <select
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent appearance-none"
              >
                <option value="all">All Agents</option>
                {workflows.map((wf) => (
                  <option key={wf.id} value={wf.workflow_id}>
                    {wf.workflow_name || `Agent ${wf.workflow_id}`}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
            </div>
          </div>
          {(fromDate || toDate) && (
            <button
              onClick={() => {
                setFromDate('');
                setToDate('');
              }}
              className="mt-3 text-sm font-medium"
              style={{ color: brand.accent }}
            >
              Clear date filters
            </button>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-base font-semibold text-text-main">
              Recent Calls ({filtered.length} of {total})
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: brand.accent }}></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Phone className="mx-auto h-12 w-12 text-text-muted/50" />
              <h3 className="mt-3 text-sm font-medium text-text-main">No calls found</h3>
              <p className="text-sm text-text-muted">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((call) => (
                <div key={call.id} className="group">
                  <div
                    onClick={() => setSelectedCall(call)}
                    className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3 mb-2">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: brand.accent + '15' }}
                          >
                            <User className="w-5 h-5" style={{ color: brand.accent }} />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-text-main">
                              {getCustomerName(call)}
                            </h3>
                            <p className="text-xs text-text-muted">Run ID: {call.workflow_run_id}</p>
                          </div>
                        </div>

                        {getDisplayFields(call).length > 0 && (
                          <div className="flex flex-wrap items-center gap-3 text-sm mb-2">
                            {getDisplayFields(call).map((field, i) => (
                              <div key={i} className="flex items-center gap-1">
                                <span className="text-base">{field.icon}</span>
                                <span className="text-text-muted">{field.label}:</span>
                                <span className="text-text-main font-medium">{field.value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDistanceToNow(new Date(call.created_at), { addSuffix: true })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatPulse(call.call_duration_seconds)}</span>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                            {call.call_disposition || 'completed'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(call.id);
                          }}
                          className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        {call.recording_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(expandedId === call.id ? null : call.id);
                            }}
                            className="p-2 rounded-lg transition-colors"
                            style={{
                              color: expandedId === call.id ? brand.accent : brand.accentHover,
                              backgroundColor: expandedId === call.id ? brand.accent + '15' : 'transparent',
                            }}
                            title={expandedId === call.id ? 'Hide recording' : 'Play recording'}
                          >
                            <Play className="w-5 h-5" />
                          </button>
                        )}
                        {call.transcript_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTranscript(call);
                            }}
                            className="p-2 rounded-lg text-text-muted hover:bg-slate-100 transition-colors"
                            title="View transcript"
                          >
                            <FileText className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedId === call.id && call.recording_url && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                      <p className="text-sm font-medium text-text-main mb-2">
                        Recording - {getCustomerName(call)}
                      </p>
                      <audio controls className="w-full" src={fullUrl(call.recording_url)}>
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {hasMore && !loading && (
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => fetchCalls(true)}
                disabled={loadingMore}
                className="w-full py-2.5 rounded-lg text-white font-medium transition-colors disabled:opacity-60"
                style={{ backgroundColor: brand.accent }}
              >
                {loadingMore ? 'Loading...' : `Load more (${total - calls.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      </main>

      {selectedCall && !transcriptOpen && (
        <CallDetails
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
          displayConfig={displayConfigs[selectedCall.workflow_id] || []}
        />
      )}

      {transcriptOpen && selectedCall && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-main">Transcript</h3>
              <button
                onClick={() => setTranscriptOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-text-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {transcriptLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: brand.accent }}></div>
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-text-main font-sans leading-relaxed">{transcript}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-text-main mb-2">Delete call?</h3>
            <p className="text-sm text-text-muted mb-6">
              This will permanently remove the call and its associated data.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 py-2.5 rounded-lg border border-slate-200 text-text-main hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="flex-1 py-2.5 rounded-lg bg-danger text-white hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {exportOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-text-main mb-1">Export calls</h3>
            <p className="text-sm text-text-muted mb-4">Choose a date range for the CSV export.</p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-text-main mb-1.5">From date</label>
                <input
                  type="date"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-main mb-1.5">To date</label>
                <input
                  type="date"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setExportOpen(false)}
                className="flex-1 py-2.5 rounded-lg border border-slate-200 text-text-main hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex-1 py-2.5 rounded-lg text-white font-medium transition-colors disabled:opacity-60"
                style={{ backgroundColor: brand.accent }}
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
